/**
 * La conclusion d'un test, déduite des seules bandes présentes.
 *
 * Le biologiste qui reporte sa lecture de paillasse ne saisit pas « positif »
 * ou « négatif » : il dit quelles bandes il a vues, et la notice du test dit
 * ce que cela signifie. Ces règles sont déjà en base, par pathologie — les
 * appliquer ici évite de lui demander une conclusion qu'il ne doit pas avoir
 * à calculer, et garantit surtout qu'un écart de conclusion avec IRIS vient
 * des bandes vues et non d'une lecture différente de la notice.
 *
 * Volontairement séparé de `interpretBlot` (BlotGallery), qui répond à une
 * autre question : à partir d'un signal, cette bande est-elle là ? Ici la
 * question est déjà tranchée — l'œil a vu ou n'a pas vu. Ce module ne connaît
 * donc ni intensité, ni proéminence, ni distance d'accrochage, seulement une
 * liste de noms. C'est aussi pour cela qu'il doit servir aux DEUX côtés de la
 * comparaison : passer les bandes d'IRIS dans le même évaluateur est la seule
 * façon de comparer des conclusions qui veulent dire la même chose.
 */

export type BandVerdict = 'POSITIF' | 'NEGATIF'

export interface BandRulesConfig {
  pathology?: string
  bands?: { specific?: Array<{ name: string; molecularWeight?: number; required?: boolean }> }
  interpretation?: {
    mode?: string
    positive?: { result?: string; criteria?: Array<Record<string, unknown>>; conclusion?: string }
    negative?: { result?: string; conclusion?: string }
  }
}

/** Les bandes du test, de la plus lourde à la plus légère — l'ordre dans lequel
 *  elles se lisent sur la bandelette, et celui dont dépendent les critères qui
 *  parlent de bandes « majeures » ou « extrêmes ». */
export const orderedBandNames = (config: BandRulesConfig | null | undefined): string[] => {
  const specific = config?.bands?.specific
  if (!specific?.length) return []
  return [...specific]
    .sort((a, b) => (b.molecularWeight ?? 0) - (a.molecularWeight ?? 0))
    .map((b) => b.name)
}

/**
 * Le test se lit-il sur la présence de bandes ?
 *
 * Les CIP-WB se lisent en comparant deux bandelettes appariées, pas en
 * cochant des bandes : ils n'ont d'ailleurs aucune bande déclarée. Une saisie
 * de paillasse n'a rien à leur proposer.
 */
export const readsOnBandPresence = (config: BandRulesConfig | null | undefined): boolean =>
  config?.interpretation?.mode !== 'paired_comparison' && orderedBandNames(config).length > 0

/**
 * Applique les critères de positivité de la pathologie à un jeu de bandes.
 *
 * Les critères sont combinés par ET quand `operator` le dit, ce qui est le cas
 * dès qu'il y en a plusieurs : « au moins 3 bandes » ET « P30 présente ».
 * Le négatif n'est pas évalué — toutes les notices le définissent comme le
 * complément du positif (« moins de 2 bandes », « absence des bandes… »), et
 * l'écrire deux fois serait s'exposer à ce que les deux ne se répondent plus.
 */
export const verdictFromBands = (
  config: BandRulesConfig | null | undefined,
  bands: Iterable<string>,
): BandVerdict | null => {
  if (!readsOnBandPresence(config)) return null
  const criteria = config?.interpretation?.positive?.criteria
  if (!criteria?.length) return null

  const present = new Set(bands)
  const ordered = orderedBandNames(config)
  const specific = present.size > 0 ? ordered.filter((name) => present.has(name)) : []

  const satisfies = (criterion: Record<string, unknown>): boolean => {
    switch (criterion.type) {
      case 'minimum_bands':
        return specific.length >= Number(criterion.value ?? 1)
      case 'required_band':
        return present.has(String(criterion.bandId ?? ''))
      // « Présence de la bande 7 kDa ET/OU 26-28 kDa » : les deux bornes de la
      // bandelette, la plus lourde et la plus légère.
      case 'extreme_bands':
        return present.has(ordered[0]) || present.has(ordered[ordered.length - 1])
      // « Présence de P30-34 OU P22-24 » : les deux bandes majeures, soit les
      // deux plus lourdes — même convention que l'interprétation d'IRIS, dont
      // ce module doit rester le miroir exact.
      case 'major_bands':
        return ordered.slice(0, 2).some((name) => present.has(name))
      default:
        // Un critère inconnu ne doit pas rendre positif par défaut : mieux vaut
        // une conclusion refusée qu'une conclusion inventée.
        return false
    }
  }

  const operator = String(config?.interpretation?.positive?.['operator' as never] ?? 'AND')
  const positive =
    operator.toUpperCase() === 'OR' ? criteria.some(satisfies) : criteria.every(satisfies)
  return positive ? 'POSITIF' : 'NEGATIF'
}

/** La phrase de la notice qui accompagne le verdict, pour l'afficher telle
 *  qu'elle est écrite plutôt que reformulée. */
export const conclusionText = (
  config: BandRulesConfig | null | undefined,
  verdict: BandVerdict | null,
): string => {
  if (!verdict) return ''
  const side = verdict === 'POSITIF' ? config?.interpretation?.positive : config?.interpretation?.negative
  return side?.conclusion ?? ''
}
